import React, { useEffect, useState } from "react";
import { Scatter } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
} from "chart.js";
import styled from "styled-components";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";

// Register Chart.js components
ChartJS.register(LinearScale, PointElement, Tooltip, Legend);

// Styled components
const ChartWrapper = styled.div`
  background-color: #f9f9f9;
  width: 100%;
  height: 75vh;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  padding: 20px;
`;

const TitleWrapper = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: bold;
  color: #666;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const InfoIconWrapper = styled.div`
  cursor: pointer;
  color: #666;
  transition: color 0.3s;
  &:hover {
    color: #000;
  }
  svg {
    width: 18px;
    height: 18px;
  }
`;

const FiltersWrapper = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 15px;
`;

const Select = styled.select`
  padding: 5px;
  font-size: 1rem;
  border: 1px solid #ccc;
  border-radius: 5px;
`;

const OutlierPurchaseTransactions = ({ zakupyData }) => {
  const [filteredData, setFilteredData] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [chartData, setChartData] = useState(null);

  const monthNames = [
    "Styczeń",
    "Luty",
    "Marzec",
    "Kwiecień",
    "Maj",
    "Czerwiec",
    "Lipiec",
    "Sierpień",
    "Wrzesień",
    "Październik",
    "Listopad",
    "Grudzień",
  ];

  // wartość transakcji zakupowej = K_42 + K_43
  const calculateTransactionValue = (row) => {
    const k42 = parseFloat(row.K_42) || 0;
    const k43 = parseFloat(row.K_43) || 0;
    return k42 + k43;
  };

  // init years
  useEffect(() => {
    if (!zakupyData || zakupyData.length === 0) return;
    const years = [...new Set(zakupyData.map((row) => parseInt(row.Rok, 10)))]
      .filter((y) => !isNaN(y))
      .sort((a, b) => b - a);
    setAvailableYears(years);
    if (years.length > 0) setSelectedYear(years[0]);
  }, [zakupyData]);

  // months for selected year
  useEffect(() => {
    if (!selectedYear) return;
    const months = [
      ...new Set(
        zakupyData
          .filter((row) => parseInt(row.Rok, 10) === selectedYear)
          .map((row) => parseInt(row.Miesiac, 10))
      ),
    ]
      .filter((m) => !isNaN(m))
      .sort((a, b) => a - b);
    setAvailableMonths(months);
  }, [selectedYear, zakupyData]);

  // filter data by year & month
  useEffect(() => {
    if (!selectedYear) return;
    const filtered = zakupyData.filter(
      (row) =>
        parseInt(row.Rok, 10) === selectedYear &&
        (selectedMonth === "all" ||
          parseInt(row.Miesiac, 10) === selectedMonth)
    );
    setFilteredData(filtered);
  }, [selectedYear, selectedMonth, zakupyData]);

  // build chart data & detect outliers via IQR
  useEffect(() => {
    if (!filteredData || filteredData.length === 0) {
      setChartData(null);
      return;
    }

    const transactions = filteredData
      .map((row, index) => ({
        id: index + 1,
        value: calculateTransactionValue(row),
        supplier: row.NazwaDostawcy || "Brak nazwy",
        document: row.DowodZakupu || "Brak numeru",
      }))
      .filter((t) => t.value > 0)
      .sort((a, b) => a.value - b.value);

    if (transactions.length < 4) {
      setChartData(null);
      return;
    }

    const values = transactions.map((t) => t.value);
    const Q1 = values[Math.floor(values.length * 0.25)];
    const Q3 = values[Math.floor(values.length * 0.75)];
    const IQR = Q3 - Q1;
    const lowerBound = Q1 - 2 * IQR;
    const upperBound = Q3 + 2 * IQR;

    const outliers = transactions.filter(
      (t) => t.value < lowerBound || t.value > upperBound
    );
    const normalTransactions = transactions.filter(
      (t) => t.value >= lowerBound && t.value <= upperBound
    );

    setChartData({
      datasets: [
        {
          label: "Normalne transakcje zakupowe",
          data: normalTransactions.map((t) => ({
            x: t.id,
            y: t.value,
            supplier: t.supplier,
            document: t.document,
          })),
          backgroundColor: "green",
          pointRadius: 4,
        },
        {
          label: "Transakcje poza normą (zakupy)",
          data: outliers.map((t) => ({
            x: t.id,
            y: t.value,
            supplier: t.supplier,
            document: t.document,
          })),
          backgroundColor: "red",
          pointRadius: 6,
        },
      ],
    });
  }, [filteredData]);

  return (
    <ChartWrapper>
      <TitleWrapper>
        Transakcje zakupowe poza normą
        <InfoIconWrapper data-tooltip-id="outlier-purchase-tooltip">
          <Info />
        </InfoIconWrapper>
      </TitleWrapper>

      <InfoTooltip
        id="outlier-purchase-tooltip"
        place="right"
        style={{
          fontSize: "12px",
          fontFamily: "Arial, sans-serif",
          color: "#fff",
        }}
        content={
          <>
            <strong>Transakcje zakupowe poza normą</strong>
            <br />
            Wykres scatter pokazuje wartości pojedynczych transakcji zakupu,
            gdzie czerwone punkty to transakcje odstające
            wykryte za pomocą IQR (rozstęp międzykwartylowy).
            <br />
            Wartość liczona jako K_42 + K_43 (tylko dodatnie).
          </>
        }
      />

      <FiltersWrapper>
        <div>
          <label>Rok: </label>
          <Select
            value={selectedYear || ""}
            onChange={(e) =>
              setSelectedYear(parseInt(e.target.value, 10) || null)
            }
          >
            {availableYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </Select>
        </div>

        <div>
          <label>Miesiąc: </label>
          <Select
            value={selectedMonth}
            onChange={(e) =>
              setSelectedMonth(
                e.target.value === "all"
                  ? "all"
                  : parseInt(e.target.value, 10)
              )
            }
          >
            <option value="all">Cały rok</option>
            {availableMonths.map((month) => (
              <option key={month} value={month}>
                {monthNames[month - 1]}
              </option>
            ))}
          </Select>
        </div>
      </FiltersWrapper>

      {chartData ? (
        <Scatter
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              x: { title: { display: true, text: "Transakcje" } },
              y: { title: { display: true, text: "Kwota (PLN)" } },
            },
            plugins: {
              legend: { position: "top" },
              tooltip: {
                callbacks: {
                  label: (context) => {
                    const value = context.parsed.y;
                    const raw = context.raw || {};
                    const lines = [
                      `Wartość: ${value.toLocaleString("pl-PL")} zł`,
                    ];
                    if (raw.supplier) {
                      lines.push(`Dostawca: ${raw.supplier}`);
                    }
                    if (raw.document) {
                      lines.push(`Dokument: ${raw.document}`);
                    }
                    return lines;
                  },
                },
              },
            },
          }}
        />
      ) : (
        <p>Brak transakcji dla wybranego okresu lub za mało danych do analizy.</p>
      )}
    </ChartWrapper>
  );
};

export default OutlierPurchaseTransactions;
